import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  CheckCircle,
  XCircle,
  Trash2,
  FolderInput,
  Copy,
  Tag,
  DollarSign,
  Loader2,
  Printer,
} from 'lucide-react';
import type { Store, Category } from '../types';

interface BulkActionsBarProps {
  selectedCount: number;
  onClear: () => void;
  onActivate: () => Promise<void>;
  onDeactivate: () => Promise<void>;
  onDelete: () => Promise<void>;
  onMove: (storeId: string) => Promise<void>;
  onDuplicate: () => Promise<void>;
  onUpdateCategory: (categoryId: string | null) => Promise<void>;
  onUpdatePrice: (type: string, value: number) => Promise<void>;
  onPrintLabels?: () => void;
  stores: Store[];
  categories: Category[];
  isLoading: boolean;
}

type ActionType = 'none' | 'move' | 'category' | 'price';

export function BulkActionsBar({
  selectedCount,
  onClear,
  onActivate,
  onDeactivate,
  onDelete,
  onMove,
  onDuplicate,
  onUpdateCategory,
  onUpdatePrice,
  onPrintLabels,
  stores,
  categories,
  isLoading,
}: BulkActionsBarProps) {
  const [activeAction, setActiveAction] = useState<ActionType>('none');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [priceType, setPriceType] = useState<'set' | 'increase' | 'decrease' | 'increase_percent' | 'decrease_percent'>('set');
  const [priceValue, setPriceValue] = useState('');

  if (selectedCount === 0) return null;

  const handleMove = async () => {
    if (!selectedStoreId) return;
    await onMove(selectedStoreId);
    setActiveAction('none');
    setSelectedStoreId('');
  };

  const handleCategory = async () => {
    await onUpdateCategory(selectedCategoryId || null);
    setActiveAction('none');
    setSelectedCategoryId('');
  };

  const handlePrice = async () => {
    const value = parseFloat(priceValue);
    if (isNaN(value)) return;
    await onUpdatePrice(priceType, value);
    setActiveAction('none');
    setPriceValue('');
  };

  const handleDelete = async () => {
    if (confirm(`Delete ${selectedCount} product(s)? This cannot be undone.`)) {
      await onDelete();
    }
  };

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-4">
        <div className="flex items-center gap-4">
          {/* Selection Info */}
          <div className="flex items-center gap-3 pr-4 border-r border-slate-700">
            <button
              onClick={onClear}
              className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
            >
              <X size={24} className="text-slate-400" />
            </button>
            <span className="text-sm font-medium text-white whitespace-nowrap">
              {selectedCount} selected
            </span>
          </div>

          {/* Actions */}
          {activeAction === 'none' ? (
            <div className="flex items-center gap-2">
              <button
                onClick={onActivate}
                disabled={isLoading}
                className="btn btn-sm bg-green-600 hover:bg-green-500 text-white"
              >
                <CheckCircle size={14} />
                Activate
              </button>
              <button
                onClick={onDeactivate}
                disabled={isLoading}
                className="btn btn-sm bg-slate-700 hover:bg-slate-600 text-white"
              >
                <XCircle size={16} />
                Deactivate
              </button>
              <button
                onClick={onDuplicate}
                disabled={isLoading}
                className="btn btn-sm bg-slate-700 hover:bg-slate-600 text-white"
              >
                <Copy size={16} />
                Duplicate
              </button>
              {stores.length > 1 && (
                <button
                  onClick={() => setActiveAction('move')}
                  disabled={isLoading}
                  className="btn btn-sm bg-slate-700 hover:bg-slate-600 text-white"
                >
                  <FolderInput size={16} />
                  Move
                </button>
              )}
              {categories.length > 0 && (
                <button
                  onClick={() => setActiveAction('category')}
                  disabled={isLoading}
                  className="btn btn-sm bg-slate-700 hover:bg-slate-600 text-white"
                >
                  <Tag size={16} />
                  Category
                </button>
              )}
              <button
                onClick={() => setActiveAction('price')}
                disabled={isLoading}
                className="btn btn-sm bg-slate-700 hover:bg-slate-600 text-white"
              >
                <DollarSign size={16} />
                Price
              </button>
              {onPrintLabels && (
                <button
                  onClick={onPrintLabels}
                  disabled={isLoading}
                  className="btn btn-sm bg-green-600 hover:bg-green-500 text-white"
                >
                  <Printer size={16} />
                  Print Labels
                </button>
              )}
              <button
                onClick={handleDelete}
                disabled={isLoading}
                className="btn btn-sm bg-red-600 hover:bg-red-500 text-white"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          ) : activeAction === 'move' ? (
            <div className="flex items-center gap-2">
              <select
                value={selectedStoreId}
                onChange={(e) => setSelectedStoreId(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm"
              >
                <option value="">Select store...</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleMove}
                disabled={!selectedStoreId || isLoading}
                className="btn btn-sm bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
              >
                {isLoading ? <Loader2 size={16} className="animate-spin" /> : 'Move'}
              </button>
              <button
                onClick={() => setActiveAction('none')}
                className="btn btn-sm bg-slate-700 hover:bg-slate-600 text-white"
              >
                Cancel
              </button>
            </div>
          ) : activeAction === 'category' ? (
            <div className="flex items-center gap-2">
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm"
              >
                <option value="">No category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.parent ? `${cat.parent.name} > ` : ''}{cat.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleCategory}
                disabled={isLoading}
                className="btn btn-sm bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
              >
                {isLoading ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
              </button>
              <button
                onClick={() => setActiveAction('none')}
                className="btn btn-sm bg-slate-700 hover:bg-slate-600 text-white"
              >
                Cancel
              </button>
            </div>
          ) : activeAction === 'price' ? (
            <div className="flex items-center gap-2">
              <select
                value={priceType}
                onChange={(e) => setPriceType(e.target.value as any)}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm"
              >
                <option value="set">Set to</option>
                <option value="increase">Increase by</option>
                <option value="decrease">Decrease by</option>
                <option value="increase_percent">Increase %</option>
                <option value="decrease_percent">Decrease %</option>
              </select>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                  {priceType.includes('percent') ? '%' : '€'}
                </span>
                <input
                  type="number"
                  value={priceValue}
                  onChange={(e) => setPriceValue(e.target.value)}
                  className="w-24 bg-slate-800 border border-slate-700 rounded-lg pl-6 pr-3 py-1.5 text-white text-sm"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <button
                onClick={handlePrice}
                disabled={!priceValue || isLoading}
                className="btn btn-sm bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
              >
                {isLoading ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
              </button>
              <button
                onClick={() => setActiveAction('none')}
                className="btn btn-sm bg-slate-700 hover:bg-slate-600 text-white"
              >
                Cancel
              </button>
            </div>
          ) : null}

          {/* Loading Indicator */}
          {isLoading && (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Loader2 size={16} className="animate-spin" />
              Processing...
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
